import React from 'react';
import { ArrowLeftRight, DoorOpen, Pencil, Trash2, Unplug } from 'lucide-react';
import { TONE_CHIP } from '../../lib/rcu/catalog/feedback';
import { LOAD_ICON_MAP } from '../../lib/rcu/catalog/icon';
import { LOAD_TYPE_SPECS } from '../../lib/rcu/catalog/loadType';
import type { Load } from '../../lib/rcu/model';
import type { ChannelTarget } from '../../lib/rcu/query/targets';

export interface LoadRowProps {
  load: Load;
  /** Name of the room it belongs to, or null when unassigned. */
  roomName: string | null;
  /** Its output, or null when unwired or the rail is gone. */
  target: ChannelTarget | null;
  onEdit: () => void;
  onAssign: () => void;
  onRemove: () => void;
}

const IconButton: React.FC<{
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    onClick={onClick}
    className="flex h-9 w-9 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#D4A054]"
  >
    {children}
  </button>
);

/** One load in a list, with its wiring and room at a glance. */
export const LoadRow: React.FC<LoadRowProps> = ({
  load,
  roomName,
  target,
  onEdit,
  onAssign,
  onRemove,
}) => {
  const Icon = LOAD_ICON_MAP[load.iconKey];

  return (
    <li className="flex items-center gap-3 rounded-2xl border border-[#E0E0E0] bg-white px-4 py-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FDF6E9] text-[#D4A054]">
        <Icon className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[#333333]">{load.name}</p>
        <p className="truncate text-xs text-[#666666]">
          {LOAD_TYPE_SPECS[load.type].label}
          {target !== null && ` · ${target.shortLabel}`}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {target === null ? (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_CHIP.warning}`}>
              <Unplug className="h-3 w-3" />
              Not wired
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[11px] font-medium text-[#666666]">
              <DoorOpen className="h-3 w-3" />
              {roomName ?? 'Unassigned'}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center">
        <IconButton label={`Wire ${load.name}`} onClick={onAssign}>
          <ArrowLeftRight className="h-4 w-4" />
        </IconButton>
        <IconButton label={`Edit ${load.name}`} onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </IconButton>
        <IconButton label={`Remove ${load.name}`} onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </IconButton>
      </div>
    </li>
  );
};

export default LoadRow;
